/**
 * Sync event shapes + pending-event log operations.
 *
 * Ported from legacy `frontend/src/types.ts` (PendingEventV2, ServerEventV2,
 * ActionTypeV2, SyncConflictV2, SyncResponseV2, FullResyncDataV2) and the
 * bookkeeping helpers in legacy `frontend/src/db/localDb.ts`
 * (addPendingEventV2, getUnsyncedEventsV2, markEventsSyncedV2, etc.).
 *
 * Wire-shape preservation: types here are byte-compatible with the legacy
 * `/api/v2/sync` protocol. Do NOT change field names or introduce new
 * required fields — the backend stays on the same contract until T10 cutover.
 *
 * CP1: no platform imports, no globals. All storage access goes through
 * `IPendingEventStorage` (via `IStorage`); all clock reads come from an
 * injected `now()` so tests and the engine share the same time source.
 */

import type {
  IPendingEventStorage,
  ISyncStateStorage,
  PendingEvent,
} from '@shopista/storage/interface';

// ---------------------------------------------------------------------------
// Action taxonomy (wire)
// ---------------------------------------------------------------------------

/** Item-level event action types. Stable wire contract with legacy backend. */
export type ItemActionTypeV2 =
  | 'item_added'
  | 'item_checked'
  | 'item_unchecked'
  | 'item_purchased'
  | 'item_deleted'
  | 'item_updated'
  | 'item_quantity_changed'
  | 'item_notes_changed';

/** Product-level event action types. Stable wire contract with legacy backend.
 *  T12.0.1.4 added per-row retailer-preference events to replace the dropped
 *  `preferred_retailer_ids` / `fallback_retailer_ids` array fields on HouseholdProduct.
 */
export type ProductActionTypeV2 =
  | 'product_created'
  | 'product_updated'
  | 'product_deleted'
  | 'product_retailer_pref_added'
  | 'product_retailer_pref_removed'
  // T12.1.7 — per-HouseholdProduct brand + store-location preference events.
  // The canonical rows live server-side (in their preference tables); the
  // client engine has no local array mirror for these, so they fall through
  // `applyHouseholdProductEventInner`'s default branch (no projection needed).
  | 'product_brand_pref_added'
  | 'product_brand_pref_removed'
  | 'product_storelocation_pref_added'
  | 'product_storelocation_pref_removed'
  // T12.1.8.2 — per-HouseholdProduct SystemProduct connection events.
  | 'product_systemproduct_pref_added'
  | 'product_systemproduct_pref_removed';

/**
 * Household-row event action types — T12.0.16. Each carries a whole-blob
 * jsonb payload via `newValue` and has no item / product target. The
 * household event channel is keyed strictly on the request's `householdId`.
 * Adding a new household-row column means adding a new action here.
 *
 * Additive extension only — wire field names stay locked per the header
 * comment on this file. No existing types are renamed or reshaped.
 */
export type HouseholdActionTypeV2 = 'household_store_preferences_changed';

/** Union of all action types sent/received by /api/v2/sync. */
export type ActionTypeV2 =
  | ItemActionTypeV2
  | ProductActionTypeV2
  | HouseholdActionTypeV2;

// ---------------------------------------------------------------------------
// Wire shapes (client → server and server → client)
// ---------------------------------------------------------------------------

/**
 * Client-side event sent to the server during push. Mirrors the legacy
 * `ClientEventV2` shape: each event targets exactly one of a shopping-list
 * item or a household-product (invariant I6 at the sync layer — never both on the
 * wire). `previousValue` was removed in T12.0.1.1 — it was destructured by
 * the backend but never read for branching/logic, and never wired up to a
 * future undo path.
 *
 * Wire field `clientSentAt` (T12.0.18.1.1) — renamed from the legacy
 * `clientTimestamp`. The local-storage `PendingEvent.clientTimestamp` keeps
 * its name (Dexie carry-over); `toClientEvent` maps to the wire name.
 */
export interface ClientEventV2 {
  shoppingListItemId?: string;
  householdproductId?: string;
  actionType: ActionTypeV2;
  newValue?: Record<string, unknown>;
  clientSentAt: string;
  deviceId: string;
  userId: string;
}

/**
 * Server-side event returned by /api/v2/sync during pull. Matches legacy
 * `ServerEventV2`. `timestamp` is the server-authoritative time the event
 * was recorded; `userId` may be null for system/seed events, so the engine
 * must tolerate that.
 */
export interface ServerEventV2 {
  shoppingListItemId?: string;
  householdproductId?: string;
  actionType: ActionTypeV2;
  newValue?: Record<string, unknown>;
  timestamp: string;
  userId: string | null;
  userName?: string;
}

/** Conflict record the server emits when LWW picks server over client. */
export interface SyncConflictV2 {
  shoppingListItemId?: string;
  householdproductId?: string;
  clientAction: ActionTypeV2;
  serverAction: ActionTypeV2;
  resolution: 'server_wins' | 'client_wins';
  reason: string;
}

/**
 * Request body posted to `/api/v2/sync`.
 *
 * Wire field `lastSyncedAt` (T12.0.18.1.1) — renamed from the legacy
 * `lastSyncTimestamp`. The local-storage `SyncState.lastSyncTimestamp` keeps
 * its name (Dexie carry-over); the engine reads the cursor from storage and
 * emits it under the wire name.
 */
export interface SyncRequestV2 {
  householdId: string;
  deviceId: string;
  userId: string;
  lastSyncedAt: string;
  events: ClientEventV2[];
}

/** Response from `/api/v2/sync`. Wire field `lastSyncedAt` matches the request shape. */
export interface SyncResponseV2 {
  success: boolean;
  newEvents: ServerEventV2[];
  conflicts: SyncConflictV2[];
  lastSyncedAt: string;
  serverTimestamp: string;
  fullResyncRequired?: boolean;
}

// ---------------------------------------------------------------------------
// Pending event (local) — constructor + log ops
// ---------------------------------------------------------------------------

/**
 * Input shape for `appendEvent`. The caller provides the business payload;
 * `appendEvent` mints the id and stamps `synced: false`.
 */
export interface NewPendingEvent {
  shoppingListItemId?: string;
  householdproductId?: string;
  actionType: ActionTypeV2;
  newValue?: Record<string, unknown>;
  clientTimestamp: string;
  deviceId: string;
  userId: string;
}

/** Injected id factory — tests pass a deterministic stub; prod passes `crypto.randomUUID`. */
export type IdFactory = () => string;

/**
 * Append a pending event to the local log. Assigns a new id, flags
 * `synced: false`. Mirrors legacy `addPendingEventV2` semantics.
 *
 * The caller supplies `clientTimestamp` because in legacy code the same
 * timestamp was stamped on the in-memory mutation and on the event — they
 * must stay in sync so the server's LWW sees the intended ordering.
 */
export async function appendEvent(
  storage: IPendingEventStorage,
  event: NewPendingEvent,
  newId: IdFactory,
): Promise<PendingEvent> {
  const record: PendingEvent = {
    id: newId(),
    actionType: event.actionType,
    clientTimestamp: event.clientTimestamp,
    deviceId: event.deviceId,
    userId: event.userId,
    synced: false,
    ...(event.shoppingListItemId !== undefined
      ? { shoppingListItemId: event.shoppingListItemId }
      : {}),
    ...(event.householdproductId !== undefined ? { householdproductId: event.householdproductId } : {}),
    ...(event.newValue !== undefined ? { newValue: event.newValue } : {}),
  };
  await storage.addEvent(record);
  return record;
}

/**
 * Read all pending (unsynced) events from the log, in insertion-stable order
 * (timestamp-ascending — clientTimestamp is ISO-8601, so lexicographic sort
 * is time-ordered). Legacy sorted in the DB query; we sort here so the
 * interface stays impl-agnostic.
 */
export async function readPendingEvents(
  storage: IPendingEventStorage,
): Promise<PendingEvent[]> {
  const events = await storage.listUnsyncedEvents();
  return events.slice().sort((a, b) => {
    if (a.clientTimestamp < b.clientTimestamp) return -1;
    if (a.clientTimestamp > b.clientTimestamp) return 1;
    // Stable tiebreaker on id so two events at the same millisecond are
    // still deterministic (important for tests).
    if (a.id < b.id) return -1;
    if (a.id > b.id) return 1;
    return 0;
  });
}

/**
 * Mark events as pushed (server acknowledged). Mirrors legacy
 * `markEventsSyncedV2`. Calling with an empty array is a no-op.
 */
export async function markEventsPushed(
  storage: IPendingEventStorage,
  eventIds: readonly string[],
): Promise<void> {
  if (eventIds.length === 0) return;
  await storage.markEventsSynced([...eventIds]);
}

/**
 * Project a `PendingEvent` onto the wire shape the server expects.
 * Strips `id`, `synced` (client-local bookkeeping) and re-shapes into
 * `ClientEventV2`. Maps the storage-side `clientTimestamp` (Dexie carry-over
 * name) onto the wire-side `clientSentAt`.
 */
export function toClientEvent(event: PendingEvent): ClientEventV2 {
  return {
    actionType: event.actionType as ActionTypeV2,
    clientSentAt: event.clientTimestamp,
    deviceId: event.deviceId,
    userId: event.userId,
    ...(event.shoppingListItemId !== undefined
      ? { shoppingListItemId: event.shoppingListItemId }
      : {}),
    ...(event.householdproductId !== undefined ? { householdproductId: event.householdproductId } : {}),
    ...(event.newValue !== undefined ? { newValue: event.newValue } : {}),
  };
}

/**
 * Garbage-collect events that have been confirmed synced. Called after a
 * successful sync to keep the pending log from growing unboundedly.
 * Mirrors legacy `clearSyncedEventsV2`.
 */
export async function purgeSyncedEvents(
  storage: IPendingEventStorage,
): Promise<void> {
  await storage.deleteSyncedEvents();
}

// ---------------------------------------------------------------------------
// Sync-state cursor operations
// ---------------------------------------------------------------------------

/** Fallback cursor when the device has never synced — matches legacy seed. */
export const EPOCH_CURSOR = '1970-01-01T00:00:00.000Z';

/**
 * Read the last sync cursor for a device. Returns `EPOCH_CURSOR` when no
 * state exists so the first sync pulls the full event history the server
 * wants to hand out (server decides via its own threshold).
 */
export async function readSyncCursor(
  storage: ISyncStateStorage,
  deviceId: string,
): Promise<string> {
  const state = await storage.getSyncState(deviceId);
  return state?.lastSyncTimestamp ?? EPOCH_CURSOR;
}

/**
 * Persist the post-sync cursor. `nowMs` is the wall-clock we record as
 * `lastSyncAt` — injected so tests can pin it.
 */
export async function writeSyncCursor(
  storage: ISyncStateStorage,
  deviceId: string,
  serverTimestamp: string,
  nowMs: number,
): Promise<void> {
  await storage.putSyncState({
    deviceId,
    lastSyncTimestamp: serverTimestamp,
    lastSyncAt: nowMs,
  });
}
